// CONTRACT:P1-CANONICAL-ENVELOPE.1.0
/** nwsSource — the LIVE NWS active-alerts source: poll the National Weather Service active
 *  alerts GeoJSON feed, map each alert feature to a clear-shell `alert` envelope. The pure
 *  data layer behind nwsCapability (the exact split as adsbSource behind adsbCapability).
 *
 *  PURITY NOTE (the SDK-purity exit gate, mirrors adsbSource): this module imports ONLY the
 *  public-SDK envelope types (../../../../packages/contracts-ts) — NO host internal, NO
 *  abac-pdp, NO pdp.ts. nwsCapability imports this file; the SDK-purity scanner whitelists it.
 *
 *  SOURCE: api.weather.gov active alerts (GeoJSON) — NO API KEY. The NWS API requires a
 *  User-Agent header (a contact string) on every request; we send one. Each Feature is a
 *  single active alert: properties.{event, severity, headline, areaDesc, effective, expires, …}
 *  + geometry that is OFTEN null (zone-referenced) or a Polygon/MultiPolygon. We reduce the
 *  polygon to a representative centroid Point (the map marker); a feature with NO geometry is
 *  DROPPED (fail-closed — can't place it). Poll on a ~60s interval; a failed poll falls back to
 *  the small bundled snapshot rather than retrying tightly.
 *
 *  CLEAR-SHELL ONLY: public weather alerts are UNCLASSIFIED — payload_mode:"clear",
 *  leakage_profile:"clear", NO sealed/encrypted inner. Affiliation is "neutral" (a natural-
 *  hazard advisory). REL is EMPTY (UNCLASSIFIED + empty REL = visible to all — matches the
 *  adsbSource UNCLASS default). */

import type { CanonicalEnvelope } from "../../../../packages/contracts-ts/src/index.ts";

/** NWS active alerts GeoJSON feed. Anonymous, but the NWS API requires a User-Agent. */
export const NWS_URL = "https://api.weather.gov/alerts/active";

/** The User-Agent the NWS API requires (a contact string identifying the app). */
export const NWS_USER_AGENT = "tak-tdf-cop (https://map.rallymap.org; contact@rallymap.org)";

/** Conservative poll interval (ms). NWS alerts change minute-to-minute; ~60s is the sweet
 *  spot. Never faster than MIN_POLL_MS. */
export const DEFAULT_POLL_MS = 60_000;
export const MIN_POLL_MS = 30_000;

/** The envelope kind NWS publishes (alerts). Exported so capability + check agree. */
export const NWS_KIND = "alert" as const;

/** A single NWS GeoJSON alert feature (the subset we read). geometry is OFTEN null. */
export interface NwsFeature {
  type: "Feature";
  id?: string;
  properties: {
    event?: string | null;
    severity?: string | null;
    headline?: string | null;
    areaDesc?: string | null;
    urgency?: string | null;
    certainty?: string | null;
    effective?: string | null;
    onset?: string | null;
    expires?: string | null;
    sent?: string | null;
    [key: string]: unknown;
  } | null;
  /** Polygon / MultiPolygon coordinates, or null (zone-referenced alert). */
  geometry:
    | { type: "Polygon"; coordinates: number[][][] }
    | { type: "MultiPolygon"; coordinates: number[][][][] }
    | { type: "Point"; coordinates: number[] }
    | null;
}

/** The NWS GeoJSON FeatureCollection response shape. */
export interface NwsResponse {
  type: "FeatureCollection";
  features: NwsFeature[] | null;
  updated?: string;
}

/** A tiny BUNDLED snapshot (one real-shaped alert with a Polygon) for the offline/dev/test
 *  fallback — embedded inline so the source stays single-file like the live feed. */
export const NWS_SNAPSHOT_RESPONSE: NwsResponse = {
  type: "FeatureCollection",
  updated: "2026-06-26T00:00:00+00:00",
  features: [
    {
      type: "Feature",
      id: "https://api.weather.gov/alerts/snapshot.1",
      properties: {
        event: "Flood Warning",
        severity: "Severe",
        headline: "Flood Warning issued June 26 for the lower river basin",
        areaDesc: "Riverside County, CA",
        urgency: "Expected",
        certainty: "Likely",
        effective: "2026-06-25T23:00:00+00:00",
        onset: "2026-06-25T23:00:00+00:00",
        expires: "2026-06-26T05:00:00+00:00",
        sent: "2026-06-25T23:00:00+00:00",
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-117.0, 33.7],
            [-116.8, 33.7],
            [-116.8, 33.9],
            [-117.0, 33.9],
            [-117.0, 33.7],
          ],
        ],
      },
    },
  ],
};

/** Flatten any Polygon/MultiPolygon/Point geometry to a flat list of [lon, lat] vertices.
 *  Returns [] for null/unsupported geometry (caller treats as unmappable). */
function vertices(geometry: NwsFeature["geometry"]): Array<[number, number]> {
  if (!geometry) return [];
  if (geometry.type === "Point") {
    const c = geometry.coordinates;
    return typeof c?.[0] === "number" && typeof c?.[1] === "number" ? [[c[0], c[1]]] : [];
  }
  const out: Array<[number, number]> = [];
  const rings =
    geometry.type === "Polygon"
      ? geometry.coordinates
      : geometry.type === "MultiPolygon"
        ? geometry.coordinates.flat()
        : [];
  for (const ring of rings) {
    for (const pt of ring) {
      if (Array.isArray(pt) && typeof pt[0] === "number" && typeof pt[1] === "number") {
        out.push([pt[0], pt[1]]);
      }
    }
  }
  return out;
}

/** The representative centroid [lon, lat] of an alert's geometry (mean of its vertices), or
 *  null when the geometry is absent/unusable. Fail-closed: a centroid-less alert is dropped
 *  (never placed at 0,0). */
export function centroidOf(geometry: NwsFeature["geometry"]): [number, number] | null {
  const vs = vertices(geometry);
  if (vs.length === 0) return null;
  let sx = 0;
  let sy = 0;
  for (const [x, y] of vs) {
    sx += x;
    sy += y;
  }
  const lon = sx / vs.length;
  const lat = sy / vs.length;
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;
  return [lon, lat];
}

/** Is an NWS feature mappable to an alert? It must reduce to a finite centroid. A
 *  geometry-less (zone-referenced) alert is DROPPED — fail-closed, never placed at (0,0). */
export function isMappable(feature: NwsFeature): boolean {
  return centroidOf(feature.geometry) !== null;
}

/** A stable ISO timestamp from an NWS ISO field; falls back to the snapshot time. */
function isoFrom(value: string | null | undefined, fallbackIso: string): string {
  if (typeof value === "string" && value) {
    const ms = Date.parse(value);
    if (Number.isFinite(ms)) return new Date(ms).toISOString();
  }
  return fallbackIso;
}

/** Map a single NWS alert feature → a clear-shell `alert` envelope at its geometry centroid.
 *  The event, severity, and headline ride in `properties`. Deterministic for a fixed
 *  (feature, snapshotIso) so re-publishing the same alert updates (not dupes). */
export function featureToEnvelope(feature: NwsFeature, snapshotIso: string): CanonicalEnvelope {
  const props = feature.properties ?? {};
  const c = centroidOf(feature.geometry);
  // featureToEnvelope is only ever called on mappable features (isMappable already proved
  // centroid != null). Fail CLOSED + loud rather than emit a null-island [0,0] phantom alert if
  // that precondition is ever violated — never default a real-world coordinate to (0,0).
  if (c === null) {
    throw new Error(`nwsSource.featureToEnvelope: non-mappable feature reached the mapper (id=${String(feature.id ?? "?")})`);
  }
  const [lon, lat] = c;
  const event = typeof props.event === "string" ? props.event : null;
  const severity = typeof props.severity === "string" ? props.severity : null;
  const headline = typeof props.headline === "string" ? props.headline : null;
  const areaDesc = typeof props.areaDesc === "string" ? props.areaDesc : null;
  const id = feature.id ?? `${event ?? "alert"}:${lon},${lat}`;

  const t = isoFrom(props.sent ?? props.effective ?? props.onset, snapshotIso);
  const start = isoFrom(props.effective ?? props.onset ?? props.sent, t);
  // NWS gives an explicit expiry — use it as the staleness window (fallback +6h).
  const stale = isoFrom(props.expires, new Date(Date.parse(t) + 6 * 3_600_000).toISOString());
  const name = event ? `${event}${areaDesc ? ` — ${areaDesc}` : ""}` : headline || "Weather alert";

  // A modest confidence from NWS certainty so the alert flows through the Living-Net grammar.
  const certaintyConf: Record<string, number> = {
    observed: 0.95,
    likely: 0.8,
    possible: 0.6,
    unlikely: 0.4,
  };
  const confidence =
    certaintyConf[(props.certainty ?? "").toString().toLowerCase()] ?? 0.7;

  return {
    id: `scop:nws:${id}`,
    schema: "scop.envelope/1.0",
    kind: NWS_KIND,
    time: t,
    valid_time: { start, stale },
    geometry: { type: "Point", coordinates: [lon, lat] },
    properties: {
      name,
      affiliation: "neutral", // a natural-hazard advisory — never hostile, never friendly
      type: "weather-alert",
      source: "nws:alerts",
      domain: "weather",
      // The HEADLINE fields: event + severity + headline (filterable / inspectable).
      event,
      severity,
      headline,
      area_desc: areaDesc,
      urgency: typeof props.urgency === "string" ? props.urgency : null,
      certainty: typeof props.certainty === "string" ? props.certainty : null,
      confidence,
    },
    security: {
      classification: "UNCLASSIFIED",
      releasability: [],
      mission: [],
      caveats: [],
      originator_control: false,
      policy_ref: "policy://defense.local",
      payload_mode: "clear",
      leakage_profile: "clear",
    },
    provenance: {
      originator: "national-weather-service",
      adapter: "nws-alerts",
      received_at: snapshotIso,
      chain: [{ adapter: "nws-alerts", at: snapshotIso }],
    },
    payload: { clear: { event, severity, headline } },
  };
}

/** Map a whole NWS response → the alert envelope set. DROPS un-mappable (geometry-less)
 *  features (fail-closed). Pure + deterministic. */
export function responseToEnvelopes(resp: NwsResponse): CanonicalEnvelope[] {
  const features = resp.features ?? [];
  const snapshotIso = isoFrom(resp.updated, new Date().toISOString());
  return features.filter(isMappable).map((f) => featureToEnvelope(f, snapshotIso));
}

/** The bundled snapshot as alert envelopes (the offline/test set). */
export function snapshotEnvelopes(): CanonicalEnvelope[] {
  return responseToEnvelopes(NWS_SNAPSHOT_RESPONSE);
}

/** Fetch the LIVE NWS active alerts. Resolves to the parsed response on a 200, or `null` on
 *  ANY failure (network error, non-200, malformed body) — the caller falls back to the
 *  snapshot. NEVER throws (offline-graceful). Sends the required User-Agent + a geo+json
 *  Accept. A `fetchImpl` override makes this testable without a live network. */
export async function fetchLiveAlerts(
  fetchImpl: typeof fetch = fetch,
  signal?: AbortSignal,
): Promise<NwsResponse | null> {
  try {
    const res = await fetchImpl(NWS_URL, {
      signal,
      headers: { "User-Agent": NWS_USER_AGENT, Accept: "application/geo+json" },
    });
    if (!res.ok) return null; // throttle / 5xx / 4xx ⇒ fall back, do not retry tightly
    const body = (await res.json()) as NwsResponse;
    if (body.features != null && !Array.isArray(body.features)) return null;
    return body;
  } catch {
    return null; // unreachable / DNS / timeout / parse error — caught, never thrown into host
  }
}
